import React, { useEffect, useState } from "react";
import { navLinks, categories } from "../mock";
import { Menu, X, ChevronDown, Search, Bookmark } from "lucide-react";
import useSiteNav from "../hooks/useSiteNav";
import useBookmarks from "../hooks/useBookmarks";
import GlobalSearch from "./GlobalSearch";
import Logo from "./Logo";

const Navbar = ({ solid = false }) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const { goHashSection, goRoute, goTop, location } = useSiteNav();
  const { count } = useBookmarks();

  const isHome = location.pathname === "/";
  const forceSolid = solid || !isHome;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const activeSolid = forceSolid || scrolled;
  const textColor = activeSolid ? "text-neutral-900" : "text-neutral-50";

  const handleLink = (link) => {
    setMobileOpen(false);
    if (link.type === "route") goRoute(link.target);
    else if (link.type === "hash") goHashSection(link.target);
  };

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
        activeSolid ? "bg-[#eceae5]/90 backdrop-blur-md py-4 shadow-sm" : "bg-transparent py-6"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 flex items-center justify-between">
        <button
          onClick={goTop}
          aria-label="20 PROOF home"
          className="shrink-0"
        >
          <Logo light={!activeSolid} className="h-20 md:h-24 w-auto" />
        </button>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-7">
          {navLinks.map((link) =>
            link.type === "dropdown" ? (
              <div key={link.label} className="relative group">
                <button
                  onClick={() => goRoute(link.target)}
                  className={`nav-link flex items-center gap-1 font-sans-ui text-[15px] tracking-wide outline-none ${textColor} transition-colors duration-500`}
                >
                  {link.label}
                  <ChevronDown size={15} className="transition-transform duration-300 group-hover:rotate-180" />
                </button>
                <div className="absolute left-1/2 -translate-x-1/2 top-full pt-4 opacity-0 invisible translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-200 z-50">
                  <div className="bg-[#eceae5] border border-neutral-300 rounded-md shadow-lg py-2 min-w-[210px]">
                    {categories.map((c) => (
                      <button
                        key={c.slug}
                        onClick={() => goRoute(`/topics/${c.slug}`)}
                        className="block w-full text-left px-5 py-2.5 font-display text-lg text-neutral-900 hover:bg-neutral-900 hover:text-white transition-colors"
                      >
                        {c.name.charAt(0) + c.name.slice(1).toLowerCase()}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <button
                key={link.label}
                onClick={() => handleLink(link)}
                className={`nav-link font-sans-ui text-[15px] tracking-wide ${textColor} transition-colors duration-500`}
              >
                {link.label}
              </button>
            )
          )}

          <div className="flex items-center gap-4 pl-1">
            <button onClick={() => setSearchOpen(true)} aria-label="Search" className={`${textColor} hover:opacity-70 transition-opacity`}>
              <Search size={19} />
            </button>
            <button onClick={() => goRoute("/saved")} aria-label="Reading list" className={`relative ${textColor} hover:opacity-70 transition-opacity`}>
              <Bookmark size={19} />
              {count > 0 && (
                <span className="absolute -top-2 -right-2 bg-neutral-900 text-white text-[10px] font-sans-ui w-4 h-4 rounded-full flex items-center justify-center">
                  {count}
                </span>
              )}
            </button>
          </div>
        </nav>

        {/* Mobile toggles */}
        <div className="flex md:hidden items-center gap-4">
          <button onClick={() => setSearchOpen(true)} aria-label="Search" className={textColor}>
            <Search size={22} />
          </button>
          <button onClick={() => goRoute("/saved")} aria-label="Reading list" className={`relative ${textColor}`}>
            <Bookmark size={22} />
            {count > 0 && (
              <span className="absolute -top-2 -right-2 bg-neutral-900 text-white text-[10px] font-sans-ui w-4 h-4 rounded-full flex items-center justify-center">
                {count}
              </span>
            )}
          </button>
          <button onClick={() => setMobileOpen((v) => !v)} className={textColor}>
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden bg-[#eceae5] border-t border-neutral-200 mt-4">
          <nav className="flex flex-col px-6 py-4">
            {navLinks.map((link) =>
              link.type === "dropdown" ? (
                <div key={link.label} className="py-3 border-b border-neutral-200">
                  <button
                    onClick={() => {
                      setMobileOpen(false);
                      goRoute(link.target);
                    }}
                    className="font-display text-2xl text-neutral-900 mb-2 text-left block"
                  >
                    Topics
                  </button>
                  <div className="flex flex-col pl-3">
                    {categories.map((c) => (
                      <button
                        key={c.slug}
                        onClick={() => {
                          setMobileOpen(false);
                          goRoute(`/topics/${c.slug}`);
                        }}
                        className="font-display text-lg text-neutral-700 text-left py-1.5"
                      >
                        {c.name.charAt(0) + c.name.slice(1).toLowerCase()}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <button
                  key={link.label}
                  onClick={() => handleLink(link)}
                  className="font-display text-left text-2xl text-neutral-900 py-3 border-b border-neutral-200"
                >
                  {link.label}
                </button>
              )
            )}
            <button
              onClick={() => {
                setMobileOpen(false);
                goRoute("/saved");
              }}
              className="font-display text-left text-2xl text-neutral-900 py-3"
            >
              Reading List {count > 0 ? `(${count})` : ""}
            </button>
          </nav>
        </div>
      )}

      <GlobalSearch open={searchOpen} onOpenChange={setSearchOpen} />
    </header>
  );
};

export default Navbar;
